import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Input } from '../../../components/input/input';
import { pageKey } from '../../../helpers/path';
import { useLang } from '../../../hooks/use-lang';
import { useQueryParams } from '../../../hooks/use-query-params';
import { filterByName } from '../../table/helpers';
import { DropdownFilterPopup } from '../dropdown-filter-popup/dropdown-filter-popup';
import s from './dropdown-filter-input.module.css';

interface Props {
  name: string;
  isOpen: boolean;
  onOpen: (state: boolean) => void;
  suggestOptions?: string[];
}

export const DropdownFilterInput = (props: Props) => {
  const { name, isOpen, onOpen, suggestOptions } = props;

  const { addQueryParams, getQueryParams } = useQueryParams();
  const [search, setSearch] = useState('');
  const { t } = useLang();

  const [initSuggests, setInitSuggests] = useState(
    suggestOptions?.length ? suggestOptions : undefined,
  );

  useEffect(() => {
    if (initSuggests?.length) return;

    setInitSuggests(suggestOptions);
  }, [suggestOptions]);

  const params = getQueryParams();
  const paramName = params[name] || '';

  useEffect(() => {
    setSearch(paramName);
  }, [paramName]);

  const suggestions = useMemo(() => {
    const filtered = search
      ? initSuggests?.filter((item) => filterByName(item, search))
      : initSuggests;

    return Array.from(new Set(filtered).values());
  }, [search, initSuggests]);

  const onSearch = (value: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(value.currentTarget.value);

  const handleOk = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent> | KeyboardEvent) => {
    addQueryParams({ [name]: search.trim(), [pageKey]: '1' });
    onOpen(false);
    e.stopPropagation();
  };

  const handleReset = () => {
    addQueryParams({ [name]: undefined, [pageKey]: '1' });
    setSearch('');
  };

  return (
    <DropdownFilterPopup
      isOpen={isOpen}
      onOpen={onOpen}
      className={s.feature}
      onReset={handleReset}
      onSubmit={handleOk}
      name={name}
    >
      <Input value={search} onChange={onSearch} placeholder={t('Поиск')} autoFocus />
      {!!suggestions?.length && (
        <ul className={s.list}>
          {suggestions?.map((item, index) => (
            <li key={item + index} tabIndex={0} className={s.item} onClick={() => setSearch(item)}>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </DropdownFilterPopup>
  );
};
